import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  type DatabaseFilterNonBooleanExpression,
  DatabaseFilterType,
  isFilterWithUsageType,
} from '@databases/types/database/view';
import { loadDependenciesAsync } from '@databases/utils/database/field-types';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { getFilteredValuesWithContext } from '@databases/utils/database/view/view';
import { loadContentRef } from '@databases/utils/redux/content/actions';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { loadJiraIssue } from '@databases/utils/redux/jira/actions';
import { loadUserById } from '@databases/utils/redux/users/actions';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../hooks/useView';

interface PrefetchTarget {
  fieldType: DatabaseFieldType;
  fieldId: string;
  value: string;
}

const VALUE_KEY_BY_TYPE: Partial<Record<DatabaseFieldType, string>> = {
  [DatabaseFieldType.USER]: 'accountId',
  [DatabaseFieldType.PAGE_LINK]: 'contentId',
  [DatabaseFieldType.JIRA_ISSUE]: 'key',
};

/** Filters that carry a DatabaseValue (i.e. { type, value }) we can extract prefetch targets from. */
const FILTER_TYPES_WITH_DATABASE_VALUE = new Set([
  DatabaseFilterType.EQUALS,
  DatabaseFilterType.GREATER,
  DatabaseFilterType.LESS,
]);

const extractPrefetchTargetsFromLeaf = (
  filter: DatabaseFilterNonBooleanExpression
): PrefetchTarget[] => {
  if (!FILTER_TYPES_WITH_DATABASE_VALUE.has(filter.type) || !('value' in filter)) {
    return [];
  }

  const filterValue = filter.value;
  if (
    typeof filterValue !== 'object' ||
    filterValue === null ||
    !('type' in filterValue) ||
    !('value' in filterValue)
  ) {
    return [];
  }

  const type = filterValue.type;
  const key = VALUE_KEY_BY_TYPE[type];
  if (!key || !Array.isArray(filterValue.value)) {
    return [];
  }

  return (filterValue.value as Record<string, string>[])
    .filter((v) => key in v)
    .map((v) => ({
      fieldType: type,
      fieldId: filter.fieldId,
      value: v[key],
    }));
};

const extractPrefetchTargets = (filter: DatabaseFilter): PrefetchTarget[] => {
  if (isFilterWithUsageType(filter)) {
    return filter.value.flatMap(extractPrefetchTargets);
  }

  return extractPrefetchTargetsFromLeaf(filter);
};

export const useFilteredValues = ({ contentId }: { contentId: string }) => {
  const { view } = useView();
  const intl = useIntl();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();

  const database = defaultStoreUtils.useEqualSelector((state) => {
    return selectDatabaseByContentIdRequired(state, { contentId });
  });

  useEffect(() => {
    if (view.settings.filter) {
      Promise.all(
        getFilteredValuesWithContext(database, view.settings.filter).map(
          async (context) => {
            await loadDependenciesAsync(
              {
                contentId: context.database.contentId,
                field: context.field,
                entryId: context.entry.id,
                value: context.value,
              },
              store,
              intl
            );
          }
        )
      );
    }
  }, [database, intl, store, view.settings.filter]);

  // Prefetch data for filter values that may no longer be in entries,
  // so fallback options resolve immediately when the filter menu opens.
  const filter = view.settings.filter;
  const targets = useMemo(() => (filter ? extractPrefetchTargets(filter) : []), [filter]);

  const fieldsById = !isBrokenDatabase(database) ? database.fieldsById : undefined;

  const targetKey = useMemo(
    () => targets.map((t) => `${t.fieldType}:${t.value}`).join(','),
    [targets]
  );

  useEffect(() => {
    for (const target of targets) {
      switch (target.fieldType) {
        case DatabaseFieldType.USER:
          dispatch(loadUserById(target.value));
          break;
        case DatabaseFieldType.PAGE_LINK:
          dispatch(loadContentRef({ contentId: target.value }));
          break;
        case DatabaseFieldType.JIRA_ISSUE: {
          const field = fieldsById?.[target.fieldId];
          if (field) {
            dispatch(
              loadJiraIssue({
                key: target.value,
                cloudId: field.settings.cloudId,
                serverId: field.settings.jiraServerId,
              })
            );
          }
          break;
        }
        default:
          break;
      }
    }
    // `targetKey` is a serialized proxy for `targets` — using the primitive string
    // avoids re-firing the effect when the array reference changes but the contents don't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, targetKey, fieldsById]);
};
